// src/components/ProtectedRoute/Admin/Dialogs/DialogCountryEditor.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Autocomplete,
  Stack,
  Chip,
  Typography,
} from "@mui/material";
import supabase from "../../../../utils/supabase";

const statusOptions = ["canonical", "synonym", "historical", "pending"];
const normalizeAltNames = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((s) => (s == null ? "" : String(s))).map((s) => s.trim()).filter(Boolean);
  }
  return String(raw)
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
};

export default function DialogCountryEditor({ open, onClose, country = null }) {
  const isEdit = !!country;
  const [name, setName] = useState("");
  const [status, setStatus] = useState("pending");
  const [iso, setIso] = useState("");
  const [parentId, setParentId] = useState("");
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [note, setNote] = useState("");
  const [geojsonName, setGeojsonName] = useState("");
  const [altNames, setAltNames] = useState([]);
  const [fieldSupport, setFieldSupport] = useState({ altNames: true, geojsonName: true });
  const [countries, setCountries] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchParentsAndDetect = async () => {
      const { data, error } = await supabase.from("countries").select("id,name,status").order("name");
      if (!error) setCountries(data || []);

      // Detect optional columns
      const support = { altNames: true, geojsonName: true };
      try {
        const { error: altErr } = await supabase.from("countries").select("alt_names").limit(1);
        if (altErr) support.altNames = false;
      } catch {
        support.altNames = false;
      }
      try {
        const { error: geoErr } = await supabase.from("countries").select("geojson_name").limit(1);
        if (geoErr) support.geojsonName = false;
      } catch {
        support.geojsonName = false;
      }
      setFieldSupport(support);
    };
    fetchParentsAndDetect();
  }, []);

  useEffect(() => {
    if (!isEdit) {
      setName("");
      setStatus("pending");
      setIso("");
      setParentId("");
      setPeriodFrom("");
      setPeriodTo("");
      setNote("");
      return;
    }
    setName(country?.name || "");
    setStatus(country?.status || "pending");
    setIso(country?.iso_a3 || "");
    setParentId(country?.parent_id || "");
    setPeriodFrom(country?.period_from || "");
    setPeriodTo(country?.period_to || "");
    setNote(country?.note || "");
    setGeojsonName(country?.geojson_name || "");
    setAltNames(normalizeAltNames(country?.alt_names || []));
  }, [isEdit, country]);

  const parentOptions = useMemo(
    () => countries.filter((c) => c.id !== country?.id),
    [countries, country?.id]
  );

  const handleSave = async () => {
    if (!name.trim()) {
      alert("Name is required");
      return;
    }
    setSaving(true);
    try {
    const payload = {
      name: name.trim(),
      status: status || "pending",
      iso_a3: iso || null,
      parent_id: parentId || null,
      period_from: periodFrom || null,
      period_to: periodTo || null,
      note: note?.trim() || null,
    };
    if (fieldSupport.geojsonName) payload.geojson_name = geojsonName?.trim() || null;
    if (fieldSupport.altNames) payload.alt_names = normalizeAltNames(altNames);
      if (isEdit) {
        const { error } = await supabase.from("countries").update(payload).eq("id", country.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("countries").insert([payload]);
        if (error) throw error;
      }
      onClose?.(true);
    } catch (e) {
      console.error(e);
      alert(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose?.(false)} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? "Edit Country" : "Add Country"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name *" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
          {fieldSupport.geojsonName && (
            <TextField
              label="GeoJSON Name"
              value={geojsonName}
              onChange={(e) => setGeojsonName(e.target.value)}
              fullWidth
              helperText="GeoJSON由来の表記など、外部ソースの名称"
            />
          )}
          {fieldSupport.altNames && (
            <Stack spacing={0.5}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={altNames}
                onChange={(_, val) => setAltNames(normalizeAltNames(val))}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Alternate names / spellings"
                    placeholder="Press Enter to add"
                    fullWidth
                    helperText="検索用に別表記を保存（例: Japan, Nippon, Nihon）"
                  />
                )}
              />
              {!altNames.length && (
                <Typography variant="caption" color="text.secondary">
                  Enter variants and press Enter to add.
                </Typography>
              )}
            </Stack>
          )}
          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            fullWidth
          >
            {statusOptions.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            options={parentOptions}
            getOptionLabel={(o) => o.name || ""}
            value={parentOptions.find((c) => c.id === parentId) || null}
            onChange={(_, v) => setParentId(v ? v.id : "")}
            renderInput={(params) => <TextField {...params} label="Parent (canonical)" />}
          />
          <TextField
            label="ISO A3"
            value={iso}
            onChange={(e) => setIso(e.target.value.toUpperCase())}
            inputProps={{ maxLength: 3 }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Period From (YYYY-MM-DD)"
              value={periodFrom}
              onChange={(e) => setPeriodFrom(e.target.value)}
              fullWidth
            />
            <TextField
              label="Period To (YYYY-MM-DD)"
              value={periodTo}
              onChange={(e) => setPeriodTo(e.target.value)}
              fullWidth
            />
          </Stack>
          <TextField
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose?.(false)} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
